import { ShoppingCard } from "@/components/shopping/ShoppingCard.tsx";
import { iconForName } from "@/components/artifacts/iconForName.ts";
import type { ArtifactComponent, LeafComponent } from "@/types/artifacts-view.ts";
import type { ReactNode } from "react";

type CardComponentData = Extract<ArtifactComponent, { type: "card" }>;

// Renderer injected as a prop (like HorizontalGroupBlock) to avoid the
// CardBlock <-> renderComponent import cycle.
type RenderLeaf = (component: LeafComponent) => ReactNode;

/**
 * Boxed vertical group — stacks its children in a subtle bordered card with an
 * optional leading icon + title. Matches the legacy "MeetingInfoBox" grouping.
 */
export const CardBlock = ({
  component,
  renderLeaf,
}: {
  component: CardComponentData;
  renderLeaf: RenderLeaf;
}) => {
  if (component.shopping) return <ShoppingCard spec={component.shopping} />;
  const Icon = component.icon ? iconForName(component.icon) : null;
  const hasHeader = !!component.title || !!Icon;

  return (
    <div className="flex flex-col gap-1.5 rounded-lg border bg-muted/40 px-3 py-2.5">
      {hasHeader ? (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          {Icon ? <Icon className="size-4" /> : null}
          {component.title ? <span>{component.title}</span> : null}
        </div>
      ) : null}
      {component.children.map((child) => (
        <div key={child.id}>{renderLeaf(child)}</div>
      ))}
    </div>
  );
};
